/**
 * Printed Orders List - HYB MASTER SHOP
 * แสดงคำสั่งซื้อที่ปริ้นแล้ว พร้อมฟีเจอร์ค้นหา, กรอง, และพิมพ์ Label
 * เปิดให้ทุกคนใช้ได้ (ไม่จำกัด Admin เท่านั้น)
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Package, RefreshCw, Search, Printer, Calendar, CheckSquare, Square, Eye, Phone, MapPin } from 'lucide-react';
import {
  fetchOrders,
  printShippingLabel,
  batchPrintShippingLabels,
  Order as ApiOrder,
  ShippingLabelResponse,
  BatchShippingLabelResponse,
} from '../api/orders';
import { printShippingLabel as printFallbackLabel } from '../components/PrintableShippingLabel';
import { PageHeader } from '../components/ui/PageHeader';
import { Surface } from '../components/ui/Surface';
import { EmptyState } from '../components/ui/EmptyState';
import { ResponsiveModal, ModalFooter, ModalButton } from '../components/ui/ResponsiveModal';
import { useIsMobileOrTablet } from '../hooks/useMediaQuery';
import { cn } from '../utils/responsive';
import { formatThaiDateShort, formatThaiDate } from '../utils/dateUtils';
import { logger } from '../utils/logger';

// Types for order view
interface OrderItem {
  id: string;
  productName: string;
  quantity: number;
  price: number;
  variant?: string;
}

interface Address {
  fullName: string;
  phone: string;
  address: string;
  district: string;
  province: string;
  postalCode: string;
}

// Extended Order type with all display fields
interface OrderView {
  id: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  totalAmount: number;
  paymentMethod?: string;
  status: string;
  createdAt: string;
  items: OrderItem[];
  shippingAddress: Address;
  trackingNumber?: string | null;
  // Financial fields
  subtotal?: number;
  shippingCost?: number;
  paymentFee?: number;
  // Discount fields
  productDiscountAmount?: number;
  productDiscountPercent?: number;
  paymentDiscountAmount?: number;
  paymentDiscountPercent?: number;
  deliveryDiscountAmount?: number;
  secretDealDiscountAmount?: number;
  secretDealDiscountPercent?: number;
  loyaltyDiscountAmount?: number;
  loyaltyDiscountPercent?: number;
  totalDiscountAmount?: number;
  isSecretDeal?: boolean;
}

// Helper function to format phone number to 08x-xxx-xxxx
const formatPhoneNumber = (phone: string): string => {
  if (!phone || phone === '-') return '-';
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith('66') && digits.length === 11) {
    digits = '0' + digits.slice(2);
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

// Map API order to view order
const mapApiOrderToView = (order: ApiOrder): OrderView => {
  const items = (order.items || []).map((item: any) => ({
    id: item.id || item.product_id || Math.random().toString(),
    productName: item.product_name || item.productName || 'สินค้า',
    quantity: item.quantity || 1,
    price: item.unit_price || item.unitPrice || item.subtotal || 0,
    variant: item.variant_name || item.variantName,
  }));

  const shippingAddress: Address = {
    fullName: order.shippingName || 'ลูกค้า',
    phone: formatPhoneNumber(order.shippingPhone || '-'),
    address: order.shippingAddressLine1 || '-',
    district: order.shippingDistrict || '-',
    province: order.shippingProvince || '-',
    postalCode: order.shippingPostalCode || '-',
  };

  return {
    id: order.id,
    orderNumber: order.orderNumber || order.id,
    customerName: shippingAddress.fullName,
    customerPhone: shippingAddress.phone,
    totalAmount: order.total || 0,
    paymentMethod: order.paymentMethod,
    status: order.status,
    createdAt: typeof order.createdAt === 'string' ? order.createdAt : (order.createdAt instanceof Date ? order.createdAt.toISOString() : new Date().toISOString()),
    items,
    shippingAddress,
    trackingNumber: order.trackingNumber || null,
    // Financial fields
    subtotal: order.subtotal || 0,
    shippingCost: order.shippingCost || 0,
    paymentFee: order.paymentFee || 0,
    // Discount fields
    productDiscountAmount: order.productDiscountAmount || 0,
    productDiscountPercent: order.productDiscountPercent || 0,
    paymentDiscountAmount: order.paymentDiscountAmount || 0,
    paymentDiscountPercent: order.paymentDiscountPercent || 0,
    deliveryDiscountAmount: order.deliveryDiscountAmount || 0,
    secretDealDiscountAmount: order.secretDealDiscountAmount || 0,
    secretDealDiscountPercent: order.secretDealDiscountPercent || 0,
    loyaltyDiscountAmount: order.loyaltyDiscountAmount || 0,
    loyaltyDiscountPercent: order.loyaltyDiscountPercent || 0,
    totalDiscountAmount: order.totalDiscountAmount || 0,
    isSecretDeal: order.isSecretDeal || false,
  };
};

// Helper function to open PDF base64 in a new tab
const openPdfInNewTab = (base64Data: string): boolean => {
  try {
    if (!base64Data) return false;

    // Clean up base64 string
    const cleanedBase64 = base64Data.trim().replace(/\s/g, '');

    const binaryString = window.atob(cleanedBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      return true;
    } else {
      URL.revokeObjectURL(url);
      return false;
    }
  } catch (error) {
    logger.error('[openPdfInNewTab] Error:', error);
    return false;
  }
}

export function QuerySystem() {
  const isMobileOrTablet = useIsMobileOrTablet();

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Batch Selection State
  const [batchMode, setBatchMode] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);

  // Order Detail Modal State
  const [selectedOrder, setSelectedOrder] = useState<OrderView | null>(null);

  // Fetch printed orders only (using hasPrinted=true filter)
  // Backend now returns complete order details (including items) to avoid N+1 query problem
  const {
    data: orders,
    isLoading,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['printed-orders-list'],
    queryFn: () => fetchOrders(100, 1, undefined, true), // hasPrinted=true
    staleTime: 60000, // 1 minute
  });

  // Filter printed orders (shipped, delivered, or has tracking number)
  const printedOrders = useMemo(() => {
    if (!orders) {
      logger.debug('[QuerySystem] No orders data');
      return [];
    }

    logger.debug('[QuerySystem] Total orders fetched:', orders.length);

    const filtered = orders.filter(order => {
      const isPrinted =
        order.status === 'shipped' ||
        order.status === 'delivered' ||
        order.status === 'printed' ||
        (order.trackingNumber != null && order.trackingNumber !== '');

      return isPrinted;
    });

    logger.debug('[QuerySystem] Filtered printed orders:', filtered.length);

    // Sort by createdAt DESC (newest first)
    return filtered.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [orders]);

  // Apply search and date filters
  const filteredOrders = useMemo(() => {
    return printedOrders.filter(order => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm ||
        order.orderNumber.toLowerCase().includes(searchLower) ||
        order.shippingName?.toLowerCase().includes(searchLower) ||
        order.shippingPhone?.includes(searchTerm);

      // Date range filter
      const orderDate = new Date(order.createdAt);
      const matchesStartDate = !startDate || orderDate >= new Date(startDate);
      const matchesEndDate = !endDate || orderDate <= new Date(endDate + 'T23:59:59');

      return matchesSearch && matchesStartDate && matchesEndDate;
    });
  }, [printedOrders, searchTerm, startDate, endDate]);

  // Single print mutation
  const printLabelMutation = useMutation({
    mutationFn: (orderId: string) => printShippingLabel(orderId),
    onSuccess: (data: ShippingLabelResponse, orderId) => {
      // Use mergedPdfBase64 or labelBase64 from response
      const pdfBase64 = data.label?.mergedPdfBase64 || data.label?.labelBase64;
      let opened = false;

      if (pdfBase64) {
        opened = openPdfInNewTab(pdfBase64);
      }

      if (!opened) {
        // Fallback to HTML label if PDF fails
        const order = orders?.find(o => o.id === orderId);
        if (order) {
          printFallbackLabel(order, data);
        }
      }

      toast.success('เปิดใบปะหน้าสำเร็จ');
    },
    onError: () => {
      toast.error('ไม่สามารถพิมพ์ใบปะหน้าได้');
    }
  });

  // Batch print mutation
  const batchPrintMutation = useMutation({
    mutationFn: (orderIds: string[]) => batchPrintShippingLabels(orderIds),
    onSuccess: (data: BatchShippingLabelResponse) => {
      if (data.mergedPdfBase64) {
        const opened = openPdfInNewTab(data.mergedPdfBase64);
        if (!opened) {
          toast.error('ไม่สามารถเปิด PDF สำหรับพิมพ์แบบหลายรายการได้ กรุณาอนุญาตป๊อปอัพ');
        }
      }
      toast.success(`พิมพ์ ${data.summary.successful} รายการสำเร็จ`);
      if (data.summary.failed > 0) {
        toast.error(`พิมพ์ไม่สำเร็จ ${data.summary.failed} รายการ`);
      }
      setSelectedOrders([]);
      setBatchMode(false);
    },
    onError: () => {
      toast.error('ไม่สามารถพิมพ์ใบปะหน้าแบบหลายรายการได้');
    }
  });

  // Selection handlers
  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(o => o.id));
    }
  };

  const handleBatchPrint = () => {
    if (selectedOrders.length === 0) {
      toast.error('กรุณาเลือกรายการที่ต้องการพิมพ์');
      return;
    }
    batchPrintMutation.mutate(selectedOrders);
  };

  // Format order status for display
  const formatStatus = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      shipped: { label: 'จัดส่งแล้ว', color: 'bg-blue-100 text-blue-700' },
      delivered: { label: 'ส่งถึงแล้ว', color: 'bg-green-100 text-green-700' },
      printed: { label: 'ปริ้นแล้ว', color: 'bg-purple-100 text-purple-700' },
    };
    return statusMap[status] || { label: status, color: 'bg-stone-100 text-stone-700' };
  };

  return (
    <div className={cn(
      "space-y-4",
      !isMobileOrTablet && "space-y-6"
    )}>
      <PageHeader
        title="คำสั่งซื้อที่ปริ้นแล้ว"
        description="รายการคำสั่งซื้อที่มี Tracking Number แล้ว พร้อมฟีเจอร์ค้นหาและพิมพ์ใบปะหน้า"
        leading={<Package className="h-5 w-5" />}
        actions={(
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className={cn(
              "inline-flex items-center gap-2 rounded-lg border border-stone-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm",
              "text-stone-700 dark:text-gray-300 font-medium transition-all duration-200",
              "hover:bg-stone-50 dark:hover:bg-gray-700 hover:shadow focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500 focus:ring-offset-2",
              isMobileOrTablet ? "px-3 py-2 text-xs" : "px-4 py-2 text-sm"
            )}
          >
            <RefreshCw className={cn(
              "h-4 w-4",
              isFetching && "animate-spin"
            )} />
            <span className={cn(isMobileOrTablet && "hidden sm:inline")}>
              {isFetching ? 'กำลังรีเฟรช...' : 'รีเฟรช'}
            </span>
          </button>
        )}
      />

      {/* Search & Filter Controls */}
      <Surface>
        <div className={cn(
          "space-y-3",
          !isMobileOrTablet && "space-y-4"
        )}>
          {/* Search Row */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
            <label className="relative flex-1">
              <span className="sr-only">ค้นหา</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="ค้นหา: เลขคำสั่งซื้อ, ชื่อ, เบอร์โทร"
                className={cn(
                  "w-full rounded-lg border border-stone-300 dark:border-gray-600 bg-white dark:bg-gray-900 pl-11 pr-4",
                  "text-stone-900 dark:text-gray-100 placeholder:text-stone-400 dark:placeholder:text-gray-500",
                  "focus:border-yellow-500 dark:focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 dark:focus:ring-orange-500",
                  "transition-all duration-200",
                  isMobileOrTablet ? "py-2.5 text-sm" : "py-3 text-base"
                )}
              />
            </label>
          </div>

          {/* Date Range Row */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
            <label className="flex items-center gap-2 flex-1">
              <Calendar className="h-4 w-4 text-stone-400 dark:text-gray-500" />
              <span className={cn(
                "text-stone-600 dark:text-gray-400 whitespace-nowrap font-medium",
                isMobileOrTablet ? "text-xs" : "text-sm"
              )}>จาก:</span>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className={cn(
                  "flex-1 rounded-lg border border-stone-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3",
                  "text-stone-900 dark:text-gray-100 focus:border-yellow-500 dark:focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 dark:focus:ring-orange-500",
                  "transition-all duration-200",
                  isMobileOrTablet ? "py-2 text-sm" : "py-2.5 text-base"
                )}
              />
            </label>
            <label className="flex items-center gap-2 flex-1">
              <span className={cn(
                "text-stone-600 dark:text-gray-400 whitespace-nowrap font-medium",
                isMobileOrTablet ? "text-xs" : "text-sm"
              )}>ถึง:</span>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className={cn(
                  "flex-1 rounded-lg border border-stone-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3",
                  "text-stone-900 dark:text-gray-100 focus:border-yellow-500 dark:focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 dark:focus:ring-orange-500",
                  "transition-all duration-200",
                  isMobileOrTablet ? "py-2 text-sm" : "py-2.5 text-base"
                )}
              />
            </label>
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className={cn(
                  "text-yellow-600 hover:text-yellow-700 font-medium transition-colors",
                  isMobileOrTablet ? "text-xs" : "text-sm"
                )}
              >
                ล้างวันที่
              </button>
            )}
          </div>

          {/* Batch Mode Controls */}
          <div className={cn(
            "flex flex-wrap items-center gap-3 pt-2 border-t border-stone-200",
            !isMobileOrTablet && "gap-4"
          )}>
            <label className="flex items-center gap-2 cursor-pointer touch-target">
              <input
                type="checkbox"
                checked={batchMode}
                onChange={e => {
                  setBatchMode(e.target.checked);
                  if (!e.target.checked) setSelectedOrders([]);
                }}
                className="h-4 w-4 rounded border-stone-300 text-yellow-500 focus:ring-yellow-400"
              />
              <span className={cn(
                "text-stone-700 font-medium",
                isMobileOrTablet ? "text-xs" : "text-sm"
              )}>โหมดเลือกหลายรายการ</span>
            </label>

            {batchMode && (
              <>
                <button
                  onClick={toggleSelectAll}
                  className={cn(
                    "text-yellow-600 hover:text-yellow-700 font-medium transition-colors touch-target",
                    isMobileOrTablet ? "text-xs" : "text-sm"
                  )}
                >
                  {selectedOrders.length === filteredOrders.length ? 'ยกเลิกทั้งหมด' : 'เลือกทั้งหมด'}
                </button>

                {selectedOrders.length > 0 && (
                  <button
                    onClick={handleBatchPrint}
                    disabled={batchPrintMutation.isPending}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-yellow-400 to-orange-500",
                      "text-white font-semibold shadow hover:from-orange-500 hover:to-yellow-400 hover:shadow-accent",
                      "transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 touch-target",
                      isMobileOrTablet ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"
                    )}
                  >
                    {batchPrintMutation.isPending ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Printer className="h-4 w-4" />
                    )}
                    พิมพ์ที่เลือก ({selectedOrders.length})
                  </button>
                )}
              </>
            )}
          </div>

          {/* Results Count */}
          <div className={cn(
            "text-stone-600 font-medium",
            isMobileOrTablet ? "text-xs" : "text-sm"
          )}>
            แสดง {filteredOrders.length} รายการ
            {(searchTerm || startDate || endDate) && ` (จากทั้งหมด ${printedOrders.length} รายการ)`}
          </div>
        </div>
      </Surface>

      {/* Orders List */}
      <Surface padding="none">
        {isLoading ? (
          <div className={cn(
            "flex items-center justify-center gap-2 text-stone-500",
            isMobileOrTablet ? "py-8" : "py-12"
          )}>
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span className={cn(
              "font-medium",
              isMobileOrTablet ? "text-xs" : "text-sm"
            )}>กำลังโหลด...</span>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className={cn(
            isMobileOrTablet ? "p-4" : "p-6"
          )}>
            <EmptyState
              icon={<Package className="h-5 w-5" />}
              title={searchTerm || startDate || endDate ? 'ไม่พบคำสั่งซื้อที่ตรงกับเงื่อนไข' : 'ยังไม่มีคำสั่งซื้อที่ปริ้นแล้ว'}
              description={searchTerm || startDate || endDate ? 'ลองเปลี่ยนเงื่อนไขการค้นหา' : 'คำสั่งซื้อที่มี Tracking Number จะแสดงที่นี่'}
            />
          </div>
        ) : isMobileOrTablet ? (
          // Mobile Card View
          <div className="divide-y divide-stone-200">
            {filteredOrders.map(order => {
              const statusInfo = formatStatus(order.status);
              const isSelected = selectedOrders.includes(order.id);

              return (
                <div
                  key={order.id}
                  className={cn(
                    "p-4 transition-colors duration-200",
                    isSelected ? "bg-yellow-50" : "hover:bg-stone-50"
                  )}
                >
                  <div className="space-y-3">
                    {/* Header Row */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        {batchMode && (
                          <button
                            onClick={() => toggleOrderSelection(order.id)}
                            className="mt-0.5 touch-target"
                          >
                            {isSelected ? (
                              <CheckSquare className="h-5 w-5 text-yellow-500" />
                            ) : (
                              <Square className="h-5 w-5 text-stone-400" />
                            )}
                          </button>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-mono font-semibold text-sm text-stone-900 truncate">
                            {order.orderNumber}
                          </p>
                          <p className="text-xs text-stone-500 mt-0.5">
                            {formatThaiDateShort(order.createdAt)}
                          </p>
                        </div>
                      </div>
                      <span className={cn(
                        "inline-block rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
                        statusInfo.color
                      )}>
                        {statusInfo.label}
                      </span>
                    </div>

                    {/* Customer Info */}
                    <div className="space-y-1">
                      <p className="text-sm text-stone-900 font-medium">
                        {order.shippingName || '-'}
                      </p>
                      <p className="text-xs text-stone-600">
                        {order.shippingPhone || '-'}
                      </p>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-stone-500 font-medium mb-0.5">ยอดรวม</p>
                        <p className="font-mono font-semibold text-emerald-600">
                          ฿{Number(order.total).toLocaleString('th-TH')}
                        </p>
                      </div>
                      <div>
                        <p className="text-stone-500 font-medium mb-0.5">Tracking</p>
                        <p className="font-mono text-stone-900 truncate">
                          {order.trackingNumber || '-'}
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedOrder(mapApiOrderToView(order))}
                        className={cn(
                          "flex-1 flex justify-center items-center gap-2 rounded-lg",
                          "border border-stone-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5",
                          "text-sm font-medium text-yellow-600 dark:text-yellow-500",
                          "shadow-sm hover:bg-yellow-50 dark:hover:bg-gray-700 hover:shadow",
                          "transition-all duration-200",
                          "touch-target"
                        )}
                      >
                        <Eye className="h-4 w-4" />
                        ดูรายละเอียด
                      </button>
                      <button
                        onClick={() => printLabelMutation.mutate(order.id)}
                        disabled={printLabelMutation.isPending}
                        className={cn(
                          "flex-1 flex justify-center items-center gap-2 rounded-lg",
                          "border border-stone-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5",
                          "text-sm font-medium text-stone-700 dark:text-gray-300",
                          "shadow-sm hover:bg-stone-50 dark:hover:bg-gray-700 hover:shadow",
                          "transition-all duration-200",
                          "disabled:cursor-not-allowed disabled:opacity-60",
                          "touch-target"
                        )}
                      >
                        {printLabelMutation.isPending ? (
                          <>
                            <RefreshCw className="h-4 w-4 animate-spin" />
                            พิมพ์...
                          </>
                        ) : (
                          <>
                            <Printer className="h-4 w-4" />
                            พิมพ์
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Desktop Table View
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-stone-200 dark:border-gray-700 bg-stone-50 dark:bg-gray-800">
                <tr>
                  {batchMode && (
                    <th className="w-12 px-4 py-3 text-left">
                      <button onClick={toggleSelectAll} className="text-stone-500 dark:text-gray-400 hover:text-stone-700 dark:hover:text-gray-300 transition-colors">
                        {selectedOrders.length === filteredOrders.length ? (
                          <CheckSquare className="h-5 w-5 text-yellow-500" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                  )}
                  <th className="px-4 py-3 text-left font-semibold text-stone-700 dark:text-gray-300">เลขที่คำสั่งซื้อ</th>
                  <th className="px-4 py-3 text-left font-semibold text-stone-700 dark:text-gray-300">ลูกค้า</th>
                  <th className="px-4 py-3 text-right font-semibold text-stone-700 dark:text-gray-300">ยอดรวม</th>
                  <th className="px-4 py-3 text-left font-semibold text-stone-700 dark:text-gray-300">Tracking</th>
                  <th className="px-4 py-3 text-left font-semibold text-stone-700 dark:text-gray-300">สถานะ</th>
                  <th className="px-4 py-3 text-left font-semibold text-stone-700 dark:text-gray-300">วันที่</th>
                  <th className="px-4 py-3 text-center font-semibold text-stone-700 dark:text-gray-300">ดู</th>
                  <th className="px-4 py-3 text-center font-semibold text-stone-700 dark:text-gray-300">พิมพ์</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200 dark:divide-gray-700">
                {filteredOrders.map(order => {
                  const statusInfo = formatStatus(order.status);
                  const isSelected = selectedOrders.includes(order.id);

                  return (
                    <tr
                      key={order.id}
                      className={cn(
                        "transition-colors duration-200",
                        isSelected ? "bg-yellow-50 dark:bg-yellow-900/20" : "hover:bg-stone-50 dark:hover:bg-gray-700/50"
                      )}
                    >
                      {batchMode && (
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleOrderSelection(order.id)}
                            className="text-stone-500 dark:text-gray-400 hover:text-stone-700 dark:hover:text-gray-300 transition-colors"
                          >
                            {isSelected ? (
                              <CheckSquare className="h-5 w-5 text-yellow-500" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <span className="font-mono font-semibold text-stone-900 dark:text-gray-100">
                          {order.orderNumber}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-stone-900 dark:text-gray-100 font-medium">{order.shippingName || '-'}</div>
                        <div className="text-xs text-stone-600 dark:text-gray-400">{order.shippingPhone || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                          ฿{Number(order.total).toLocaleString('th-TH')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-stone-700 dark:text-gray-400">
                          {order.trackingNumber || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          "inline-block rounded-full px-2 py-0.5 text-xs font-medium",
                          statusInfo.color
                        )}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-stone-700 dark:text-gray-400">
                          {formatThaiDate(order.createdAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setSelectedOrder(mapApiOrderToView(order))}
                          className="p-2 rounded-lg hover:bg-yellow-50 dark:hover:bg-gray-700 transition-colors"
                          title="ดูรายละเอียด"
                        >
                          <Eye className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => printLabelMutation.mutate(order.id)}
                          disabled={printLabelMutation.isPending}
                          className={cn(
                            "inline-flex items-center gap-1 rounded-lg",
                            "border border-stone-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5",
                            "text-xs font-medium text-stone-700 dark:text-gray-300",
                            "shadow-sm hover:bg-stone-50 dark:hover:bg-gray-700 hover:shadow",
                            "transition-all duration-200",
                            "disabled:cursor-not-allowed disabled:opacity-60"
                          )}
                          title="พิมพ์ใบปะหน้า"
                        >
                          {printLabelMutation.isPending ? (
                            <RefreshCw className="h-3 w-3 animate-spin" />
                          ) : (
                            <Printer className="h-3 w-3" />
                          )}
                          พิมพ์
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Surface>

      {/* Order Detail Modal */}
      <ResponsiveModal
        isOpen={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        title={`รายละเอียดคำสั่งซื้อ #${selectedOrder?.orderNumber || ''}`}
        size="lg"
        mobileStyle="fullscreen"
        footer={
          selectedOrder && (
            <ModalFooter>
              <ModalButton variant="secondary" onClick={() => setSelectedOrder(null)}>
                ปิด
              </ModalButton>
              <ModalButton
                variant="primary"
                onClick={() => {
                  printLabelMutation.mutate(selectedOrder.id);
                  setSelectedOrder(null);
                }}
                disabled={printLabelMutation.isPending}
              >
                <Printer className="w-4 h-4 inline mr-2" />
                พิมพ์ใบปะหน้า
              </ModalButton>
            </ModalFooter>
          )
        }
      >
        {selectedOrder && (
          <div className="space-y-6">
            {/* Customer Info */}
            <div>
              <h4 className="font-medium text-stone-900 dark:text-white mb-3 text-sm">ข้อมูลลูกค้า</h4>
              <div className="bg-stone-50 dark:bg-gray-900 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-stone-600 dark:text-stone-400">ชื่อ:</span>
                  <span className="text-stone-900 dark:text-white">{selectedOrder.customerName}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-stone-500" />
                  <span className="font-mono text-stone-900 dark:text-white">{selectedOrder.customerPhone}</span>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div>
              <h4 className="font-medium text-stone-900 dark:text-white mb-3 text-sm flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                ที่อยู่จัดส่ง
              </h4>
              <div className="bg-stone-50 dark:bg-gray-900 rounded-lg p-4 space-y-1 text-sm">
                <p className="font-medium text-stone-900 dark:text-white">{selectedOrder.shippingAddress.fullName}</p>
                <p className="text-stone-600 dark:text-stone-400 font-mono">{selectedOrder.shippingAddress.phone}</p>
                <p className="text-stone-700 dark:text-stone-300 leading-relaxed">
                  {selectedOrder.shippingAddress.address}<br />
                  {selectedOrder.shippingAddress.district} {selectedOrder.shippingAddress.province} {selectedOrder.shippingAddress.postalCode}
                </p>
                {selectedOrder.trackingNumber && (
                  <div className="mt-3 pt-3 border-t border-stone-200 dark:border-stone-600">
                    <p className="text-sm font-medium text-yellow-700 dark:text-yellow-500 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      เลขติดตาม: <span className="font-mono">{selectedOrder.trackingNumber}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Order Items */}
            <div>
              <h4 className="font-medium text-stone-900 dark:text-white mb-3 text-sm">รายการสินค้า</h4>
              <div className="space-y-2">
                {selectedOrder.items.map((item) => (
                  <div key={item.id} className="flex justify-between items-start bg-stone-50 dark:bg-stone-700 rounded-lg p-3">
                    <div className="flex-1">
                      <p className="font-medium text-sm text-stone-900 dark:text-white">{item.productName}</p>
                      {item.variant && (
                        <p className="text-xs text-stone-600 dark:text-stone-400 mt-0.5">{item.variant}</p>
                      )}
                      <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
                        จำนวน: <span className="font-mono">{item.quantity}</span> ชิ้น
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-mono font-bold text-sm text-emerald-600">
                        ฿{(item.price * item.quantity).toLocaleString('th-TH')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary Section */}
              <div className="mt-4 pt-4 border-t border-stone-200 dark:border-stone-600">
                <h4 className="font-medium text-stone-900 dark:text-white mb-3 text-sm">📋 สรุปคำสั่งซื้อ</h4>
                <div className="bg-stone-50 dark:bg-stone-700 rounded-lg p-4 space-y-2 text-sm">
                  {/* 1. ยอดรวมสินค้า */}
                  <div className="flex justify-between items-center">
                    <span className="text-stone-600 dark:text-stone-400">ยอดรวมสินค้า ({selectedOrder.items.length} รายการ):</span>
                    <span className="font-mono text-stone-900 dark:text-white">฿{(selectedOrder.subtotal ?? 0).toLocaleString('th-TH')}</span>
                  </div>

                  {/* 2. ส่วนลดสินค้า */}
                  {(selectedOrder.productDiscountAmount ?? 0) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-stone-600 dark:text-stone-400">
                        ส่วนลดสินค้า ({selectedOrder.productDiscountPercent}%):
                      </span>
                      <span className="font-mono text-red-500">-฿{(selectedOrder.productDiscountAmount ?? 0).toLocaleString('th-TH')}</span>
                    </div>
                  )}

                  {/* 3. ส่วนลดรูปแบบการชำระเงิน */}
                  {(selectedOrder.paymentDiscountAmount ?? 0) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-stone-600 dark:text-stone-400">
                        ส่วนลดรูปแบบการชำระเงิน ({selectedOrder.paymentDiscountPercent}%):
                      </span>
                      <span className="font-mono text-red-500">-฿{(selectedOrder.paymentDiscountAmount ?? 0).toLocaleString('th-TH')}</span>
                    </div>
                  )}

                  {/* 4. ส่วนลดสมาชิก (MY Level) */}
                  {(selectedOrder.loyaltyDiscountAmount ?? 0) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-stone-600 dark:text-stone-400">
                        ส่วนลดสมาชิก ({selectedOrder.loyaltyDiscountPercent}%):
                      </span>
                      <span className="font-mono text-red-500">-฿{(selectedOrder.loyaltyDiscountAmount ?? 0).toLocaleString('th-TH')}</span>
                    </div>
                  )}

                  {/* 5. ส่วนลดดีลลับ */}
                  {(selectedOrder.secretDealDiscountAmount ?? 0) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-stone-600 dark:text-stone-400">
                        ส่วนลดดีลลับ ({selectedOrder.secretDealDiscountPercent}%):
                      </span>
                      <span className="font-mono text-red-500">-฿{(selectedOrder.secretDealDiscountAmount ?? 0).toLocaleString('th-TH')}</span>
                    </div>
                  )}

                  {/* 6. ค่าจัดส่ง */}
                  <div className="flex justify-between items-center">
                    <span className="text-stone-600 dark:text-stone-400">ค่าจัดส่ง:</span>
                    <span className={`font-mono ${(selectedOrder.shippingCost ?? 0) === 0 ? 'text-emerald-600' : 'text-stone-900 dark:text-white'}`}>
                      {(selectedOrder.shippingCost ?? 0) === 0 ? 'ฟรี' : `฿${(selectedOrder.shippingCost ?? 0).toLocaleString('th-TH')}`}
                    </span>
                  </div>

                  {/* 7. ส่วนลดค่าจัดส่ง */}
                  {(selectedOrder.deliveryDiscountAmount ?? 0) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-stone-600 dark:text-stone-400">ส่วนลดค่าจัดส่ง:</span>
                      <span className="font-mono text-red-500">-฿{(selectedOrder.deliveryDiscountAmount ?? 0).toLocaleString('th-TH')}</span>
                    </div>
                  )}

                  {/* 8. ค่าธรรมเนียม (COD) */}
                  {(selectedOrder.paymentFee ?? 0) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-stone-600 dark:text-stone-400">ค่าธรรมเนียม:</span>
                      <span className="font-mono text-stone-900 dark:text-white">฿{(selectedOrder.paymentFee ?? 0).toLocaleString('th-TH')}</span>
                    </div>
                  )}

                  {/* 9. ยอดรวมทั้งสิ้น */}
                  <div className="flex justify-between items-center pt-3 mt-2 border-t border-stone-300 dark:border-stone-500">
                    <span className="font-semibold text-stone-900 dark:text-white">ยอดรวมทั้งสิ้น:</span>
                    <span className="text-xl font-mono font-bold text-emerald-600">
                      ฿{selectedOrder.totalAmount.toLocaleString('th-TH')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </ResponsiveModal>
    </div>
  );
}
